import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Slide from "@mui/material/Slide";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "@mui/material";
import Button, { BUTTON_TYPE_CLASSES } from "../button/button.component";
import Quantity from "../shared/quantity/quantity.component";
import { TransitionsModal } from "../menu-item-modal/menu-item-modal.component";
import { addKitchenIdToCart, setCartItems } from "../../store/cart/cart.action";
import { selectCartItems } from "../../store/cart/cart.selector";
import { selectOnlineOrdersConfigs } from "../../store/kitchen/kitchen.selector";
import {
  getColor,
  getFontName,
  isOnlineOrderConfigValid,
} from "../../utils/utils.common";
import {
  ModalContainer,
  ModalScrollContainer,
  IconContainer as ModalIconContainer,
  DishName,
  DishNameEllips,
  DescriptionText,
  PromoAvailabilityText,
  TextBox,
  DishPrice,
  QuantityContainer,
} from "../menu-item-modal/menu-item-modal.styles";
import {
  formatPromoAvailabilityText,
  isDishPromoUnavailable,
} from "../../utils/promoCheck";
import {
  NavigationContainer as StepHeaderContainer,
  IconContainer as StepHeaderIconContainer,
  MobileTitle as StepHeaderTitle,
} from "../../routes/navigation/navigation.styles";
import {
  BackgroundImage,
  DescriptionContainer as CardDescription,
  ImageSkeleton,
  ImageContainer,
  MainContainer,
  MenuItemContainer,
  NameContainer,
  PriceContainer,
  SoldOutContainer,
  SoldOutCircle,
  SoldOutText,
  TextTopBox,
} from "../menu-item/menu-item.styles";
import { ArowSmallIcon, CloseIcon, LeftIcon, SuccessIcon } from "../icons";
import { dishSoldOut } from "../../constants";
import Search from "../shared/search/search.component";
import Status from "../shared/status/status.component";
import {
  businessOpenCheck,
  findNextOpening,
} from "../../utils/closedKitchenCheck";
import {
  computeStepAllocatedPrices,
  getBundleStepLineExtras,
  getBundleStepsFromConfig,
  getOptionalVariantChargesTotal,
  parseBundleMoney,
  parseStepPriceAdjustment,
  resolveBundleStepBaseWeight,
} from "../../utils/bundleAllocatedPrice";

const randomId = () => Math.random().toString(36).substring(2, 10);
const parseNumber = (value, fallback = 0) => {
  const parsed = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const formatCurrency = (amount) => `$${parseNumber(amount).toFixed(2)}`;
const formatPriceAdjustment = (value) => {
  const num = parseNumber(value, 0);
  if (!num) return null;
  return num < 0 ? `-$${Math.abs(num).toFixed(2)}` : `+$${num.toFixed(2)}`;
};
const formatVariantCharge = (value) => {
  const num = parseNumber(value, 0);
  if (!num) return null;
  return num < 0 ? `-$${Math.abs(num).toFixed(2)}` : `+$${num.toFixed(2)}`;
};

const normalizeDishes = (rawDishes) => {
  if (!rawDishes) return [];
  if (Array.isArray(rawDishes)) {
    return rawDishes.filter((dish) => dish && typeof dish === "object");
  }
  if (typeof rawDishes === "object") {
    return Object.entries(rawDishes)
      .map(([key, dish]) => {
        if (!dish || typeof dish !== "object") return null;
        return { ...dish, id: dish.id || key };
      })
      .filter(Boolean);
  }
  return [];
};

const getBundleSteps = (product) => {
  if (!product?.bundleConfig) return [];
  if (Array.isArray(product.bundleConfig.Steps)) return product.bundleConfig.Steps;
  if (Array.isArray(product.bundleConfig.steps)) return product.bundleConfig.steps;
  return [];
};

const getStepId = (step, index) => step?.id || step?.stepId || `step-${index}`;
const getStepName = (step, index) =>
  step?.stepName || step?.name || step?.title || `Step ${index + 1}`;

const getAvailableDishesForStep = (step, dishes) => {
  const subItems = Array.isArray(step?.subItems) ? step.subItems : [];
  const sourceType = step?.sourceType || "mixed";
  const selectableDishes = dishes.filter(
    (dish) => !(dish?.isBundle === true || dish?.bundleConfig)
  );
  const sortByDishName = (items) =>
    [...items].sort((a, b) =>
      String(a?.dishName || "").localeCompare(String(b?.dishName || ""), undefined, {
        sensitivity: "base",
      })
    );

  if (sourceType === "items") {
    const itemIds = new Set(subItems.map((subItem) => subItem?.id).filter(Boolean));
    return sortByDishName(selectableDishes.filter((dish) => itemIds.has(dish?.id)));
  }

  if (sourceType === "categories") {
    const categoryIds = new Set(
      subItems.map((subItem) => subItem?.id).filter(Boolean)
    );
    return sortByDishName(
      selectableDishes.filter((dish) => categoryIds.has(dish?.categoryId))
    );
  }

  const itemIds = new Set();
  const categoryIds = new Set();
  subItems.forEach((subItem) => {
    if (!subItem?.id) return;
    if (subItem?.type === "category" || subItem?.type === "Category") {
      categoryIds.add(subItem.id);
    } else {
      itemIds.add(subItem.id);
    }
  });

  return sortByDishName(
    selectableDishes.filter(
      (dish) => itemIds.has(dish?.id) || categoryIds.has(dish?.categoryId)
    )
  );
};

const findPriceAdjustment = (step, dish) => {
  const matchingSubItem = findMatchingSubItem(step, dish);
  if (matchingSubItem && matchingSubItem?.priceAdjustment !== undefined) {
    return parseNumber(matchingSubItem.priceAdjustment, 0);
  }
  return 0;
};

const findMatchingSubItem = (step, dish) => {
  const subItems = Array.isArray(step?.subItems) ? step.subItems : [];
  return (
    subItems.find((subItem) => subItem?.id === dish?.id) ||
    subItems.find(
      (subItem) =>
        (subItem?.type === "category" || subItem?.type === "Category") &&
        subItem?.id === dish?.categoryId
    ) ||
    null
  );
};

const getLockedModifiersForDish = (step, dish) => {
  const matchingSubItem = findMatchingSubItem(step, dish);
  const lockedOptions = matchingSubItem?.modifierRules?.lockedOptions;
  if (!Array.isArray(lockedOptions) || !Array.isArray(dish?.variants)) return [];

  return lockedOptions.filter(
    (rule) =>
      // Backward compatible: if `locked` is missing, treat as locked.
      (rule?.locked ?? true) === true &&
      Boolean(rule?.variantId) &&
      dish.variants.some((variant) => variant?.id === rule.variantId)
  );
};

const getNestedSplitExtrasTotal = (dish) => {
  if (!(dish?.isBundle === true && dish?.bundleConfig?.isSplit === true)) {
    return 0;
  }
  const nestedSelections = Array.isArray(dish?.bundleSelections)
    ? dish.bundleSelections
    : [];
  return nestedSelections.reduce((sum, nestedSelection) => {
    if (!nestedSelection?.selectedItem) return sum;
    const nestedAdjustment = parseStepPriceAdjustment(
      nestedSelection.selectedItem.priceAdjustment
    );
    const nestedVariantCharges = getOptionalVariantChargesTotal(
      Array.isArray(nestedSelection.selectedItem.variants)
        ? nestedSelection.selectedItem.variants
        : nestedSelection.selectedItem.variantSelected || []
    );
    return sum + nestedAdjustment + nestedVariantCharges;
  }, 0);
};

const findBundleStepBySelectionStepId = (bundleSteps, stepId) => {
  return (
    bundleSteps.find((step, index) => getStepId(step, index) === stepId) || null
  );
};

export const BundleModal = ({ product, showModal, toggleShowModal, kitchen }) => {
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const contentRef = useRef(null);
  const [hasBorder, setHasBorder] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [currentView, setCurrentView] = useState("steps");
  const [showValidation, setShowValidation] = useState(false);
  const [activeStepId, setActiveStepId] = useState(null);
  const [stepSelections, setStepSelections] = useState({});
  const [selectedDishForModal, setSelectedDishForModal] = useState(null);
  const [isStepItemModalOpen, setIsStepItemModalOpen] = useState(false);
  const [pickerSearchTerm, setPickerSearchTerm] = useState("");
  const [isPickerSearchOpen, setIsPickerSearchOpen] = useState(false);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [pickerImageLoadedMap, setPickerImageLoadedMap] = useState({});

  const headerModalBackground = getColor(
    "header-modal-background",
    onlineOrdersConfig
  );
  const brandAccent = getColor("brand-accent", onlineOrdersConfig);
  const backgroundTertiary = getColor("background-tertiary", onlineOrdersConfig);
  const textBody = getColor("text-body", onlineOrdersConfig);
  const brandTertiary = getColor("brand-tertiary", onlineOrdersConfig);
  const brandPrimary = getColor("brand-primary", onlineOrdersConfig);
  const backgroundPrimary = getColor("background-primary", onlineOrdersConfig);
  const backgroundSecondary = getColor("background-secondary", onlineOrdersConfig);
  const fieldBorder = getColor("field-border", onlineOrdersConfig);
  const fieldBackground = getColor("field-background", onlineOrdersConfig);
  const fieldPlaceholderText = getColor(
    "field-placeholder-text",
    onlineOrdersConfig
  );
  const fieldFilledText = getColor("field-filled-text", onlineOrdersConfig);
  const cardBackground = getColor("card-background", onlineOrdersConfig);
  const brandFontStyle = getFontName("brand-font-style", onlineOrdersConfig);
  const isKitchenOpen = businessOpenCheck(
    kitchen?.hours?.schedule ?? null,
    kitchen?.timezone
  );
  const findNextOpeningTime = findNextOpening(
    kitchen?.hours?.schedule ?? null,
    kitchen?.timezone
  );
  const skeletonBase = cardBackground;
  const skeletonHighlight =
    backgroundPrimary?.toLowerCase?.() === "#000000"
      ? "rgba(255, 255, 255, 0.18)"
      : "rgba(0, 0, 0, 0.06)";

  const steps = useMemo(() => getBundleSteps(product), [product]);
  const bundlePromoAvailabilityText = useMemo(
    () => formatPromoAvailabilityText(product?.promo),
    [product?.promo]
  );
  const dishes = useMemo(() => normalizeDishes(kitchen?.dishes), [kitchen?.dishes]);
  const normalizedSteps = useMemo(
    () =>
      steps.map((step, index) => ({
        ...step,
        _stepId: getStepId(step, index),
        _stepName: getStepName(step, index),
      })),
    [steps]
  );
  const selectedStepId = activeStepId || normalizedSteps[0]?._stepId || null;
  const selectedStep =
    normalizedSteps.find((step) => step._stepId === selectedStepId) || null;
  const availableDishes = useMemo(
    () => (selectedStep ? getAvailableDishesForStep(selectedStep, dishes) : []),
    [selectedStep, dishes]
  );
  const filteredAvailableDishes = useMemo(() => {
    const term = pickerSearchTerm.trim().toLowerCase();
    if (!term) return availableDishes;
    return availableDishes.filter((dish) => {
      const name = String(dish?.dishName || "").toLowerCase();
      const description = String(dish?.dishDescription || "").toLowerCase();
      return name.includes(term) || description.includes(term);
    });
  }, [availableDishes, pickerSearchTerm]);

  const isAllStepsSelected = useMemo(
    () =>
      normalizedSteps.length > 0 &&
      normalizedSteps.every((step) => Boolean(stepSelections[step._stepId]?.dish)),
    [normalizedSteps, stepSelections]
  );

  useEffect(() => {
    if (!normalizedSteps.length) return;

    setStepSelections((prevSelections) => {
      let hasChanges = false;
      const nextSelections = { ...prevSelections };

      normalizedSteps.forEach((step) => {
        // Preserve any existing selection for this step.
        if (nextSelections[step._stepId]?.dish) return;

        const availableForStep = getAvailableDishesForStep(step, dishes);
        if (availableForStep.length !== 1) return;

        const singleDish = availableForStep[0];
        const hasMandatoryModifiers = Array.isArray(singleDish?.variants)
          ? singleDish.variants.some((variant) => variant?.mandatoryChoice === true)
          : false;
        const hasStockIssue =
          singleDish?.stockDetails?.isEnabled &&
          singleDish?.stockDetails?.quantity !== null &&
          singleDish?.stockDetails?.quantity !== undefined &&
          Number(singleDish?.stockDetails?.quantity) <= 0;
        const isDishSoldOut = singleDish?.dishStatus === dishSoldOut || hasStockIssue;

        // Match mobile behavior: only auto-select if no mandatory modifiers and item is available.
        if (
          hasMandatoryModifiers ||
          isDishSoldOut ||
          isDishPromoUnavailable(singleDish)
        ) {
          return;
        }

        hasChanges = true;
        nextSelections[step._stepId] = {
          stepId: step._stepId,
          stepName: step._stepName,
          dish: singleDish,
          priceAdjustment: findPriceAdjustment(step, singleDish),
          variantSelected: [],
          note: null,
        };
      });

      return hasChanges ? nextSelections : prevSelections;
    });
  }, [normalizedSteps, dishes]);

  useEffect(() => {
    setHeroImageLoaded(false);
  }, [product?.remoteUri, showModal]);

  useEffect(() => {
    setPickerImageLoadedMap({});
  }, [selectedStepId, pickerSearchTerm]);

  const calculatedTotal = useMemo(() => {
    const bundleBase = parseNumber(product?.dishPrice, 0);
    const adjustments = normalizedSteps.reduce((sum, step) => {
      const selection = stepSelections[step._stepId];
      return sum + parseNumber(selection?.priceAdjustment, 0);
    }, 0);
    const variantCharges = normalizedSteps.reduce((sum, step) => {
      const selection = stepSelections[step._stepId];
      const charges =
        selection?.variantSelected?.reduce((variantSum, variant) => {
          if (variant?.isLocked) return variantSum;
          if (variant?.extraCharge && variant?.charge) {
            return variantSum + parseNumber(variant.charge, 0);
          }
          return variantSum;
        }, 0) || 0;
      return sum + charges;
    }, 0);
    return (bundleBase + adjustments + variantCharges) * quantity;
  }, [normalizedSteps, stepSelections, product?.dishPrice, quantity]);

  const isDishOutOfStock =
    product?.stockDetails?.isEnabled && product?.stockDetails?.quantity === 0;
  const soldOut = product?.dishStatus === dishSoldOut || isDishOutOfStock;
  const bundlePromoUnavailable = isDishPromoUnavailable(product);
  const cannotOrderBundle = soldOut || bundlePromoUnavailable;
  const unavailableButtonLabel = soldOut ? "Sold Out" : "Unavailable";
  const increment = () => setQuantity((prev) => prev + 1);
  const decrement = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  const handleModalClose = (_, reason) => {
    if (!isMobile && reason === "backdropClick") return;
    toggleShowModal();
  };

  const onStepPress = (step) => {
    setActiveStepId(step._stepId);
    setCurrentView("picker");
    setPickerSearchTerm("");
    setIsPickerSearchOpen(false);
    setShowValidation(false);
  };

  const onStepItemPress = (dish) => {
    const dishOutOfStock =
      dish?.stockDetails?.isEnabled && Number(dish?.stockDetails?.quantity) === 0;
    const isDishSoldOut = dish?.dishStatus === dishSoldOut || dishOutOfStock;
    if (isDishSoldOut || isDishPromoUnavailable(dish)) return;

    const stepSelection = stepSelections[selectedStep?._stepId];
    const hasExistingSelection = stepSelection?.dish?.id === dish?.id;
    const lockedModifiers = selectedStep ? getLockedModifiersForDish(selectedStep, dish) : [];
    const priceAdjustment = selectedStep ? findPriceAdjustment(selectedStep, dish) : 0;
    const itemForModifier = {
      ...dish,
      variantSelected: hasExistingSelection ? stepSelection?.variantSelected || [] : [],
      note: hasExistingSelection ? stepSelection?.note || null : null,
      _lockedModifiers: lockedModifiers,
      _priceAdjustment: priceAdjustment,
    };
    setSelectedDishForModal(itemForModifier);
    setIsStepItemModalOpen(true);
  };

  const onStepItemConfigured = ({ product: configuredProduct, variantSelected, note }) => {
    if (!selectedStep) return;
    const priceAdjustment = findPriceAdjustment(selectedStep, configuredProduct);
    setStepSelections((prev) => ({
      ...prev,
      [selectedStep._stepId]: {
        stepId: selectedStep._stepId,
        stepName: selectedStep._stepName,
        dish: configuredProduct,
        priceAdjustment,
        variantSelected: variantSelected || [],
        note: note || null,
      },
    }));
    setIsStepItemModalOpen(false);
    setSelectedDishForModal(null);
    setCurrentView("steps");
    setPickerSearchTerm("");
    setIsPickerSearchOpen(false);
    setShowValidation(false);
  };

  const buildSingleBundleInstance = () => {
    const bundleSteps = getBundleStepsFromConfig(product?.bundleConfig);
    const bundleBasePrice = parseBundleMoney(product?.dishPrice);
    const bundleSignature = `${product?.id}-${Date.now()}-${randomId()}`;
    const parentCartItemId = randomId();
    const isSplit = product?.bundleConfig?.isSplit === true;

    const selectedSteps = normalizedSteps
      .map((step) => stepSelections[step._stepId])
      .filter(Boolean);

    const priceAdjustmentsTotal = selectedSteps.reduce((sum, selection) => {
      const nestedSplitExtras = getNestedSplitExtrasTotal(selection?.dish);
      return (
        sum +
        parseStepPriceAdjustment(selection?.priceAdjustment) +
        nestedSplitExtras
      );
    }, 0);
    const modifierChargesTotal = selectedSteps.reduce(
      (sum, selection) =>
        sum + getOptionalVariantChargesTotal(selection?.variantSelected || []),
      0
    );
    const parentTotal =
      bundleBasePrice + priceAdjustmentsTotal + modifierChargesTotal;

    const sumBaseWeights = selectedSteps.reduce((sum, selection) => {
      const bundleStep = findBundleStepBySelectionStepId(
        bundleSteps,
        selection?.stepId
      );
      return (
        sum +
        resolveBundleStepBaseWeight(
          selection?.dish,
          selection?.variantSelected || [],
          bundleStep
        )
      );
    }, 0);
    const selectedStepCount = selectedSteps.length;

    const parentItem = {
      ...product,
      cartItemId: parentCartItemId,
      quantity: 1,
      dishPrice: bundleBasePrice,
      dishPriceWithVariants: parentTotal,
      isBundle: true,
      bundleTemplateId: product?.id,
      bundleSignature,
      note: null,
      variantSelected: [],
      isSplit,
      description: product?.description || product?.dishDescription || "",
      category: product?.category || "",
      categoryId: product?.categoryId || "",
    };

    const subItems = selectedSteps.flatMap((selection) => {
      const { dish, priceAdjustment, variantSelected = [], note } = selection;
      const adjustmentValue = parseStepPriceAdjustment(priceAdjustment);
      const bundleStep = findBundleStepBySelectionStepId(
        bundleSteps,
        selection?.stepId
      );
      const baseWeight = resolveBundleStepBaseWeight(
        dish,
        variantSelected,
        bundleStep
      );
      const lineExtras = getBundleStepLineExtras(priceAdjustment, variantSelected);
      const { allocatedPrice, allocatedPriceWithVariants } = computeStepAllocatedPrices({
        bundleBasePrice,
        baseWeight,
        sumBaseWeights,
        selectedStepCount,
        lineExtras,
      });

      const subItem = {
        id: dish?.id,
        dishName: dish?.dishName,
        dishDocketName: dish?.dishDocketName || dish?.dishName,
        description: dish?.description || dish?.dishDescription || "",
        category: dish?.category || "",
        categoryId: dish?.categoryId || "",
        variants: dish?.variants || [],
        cartItemId: randomId(),
        quantity: 1,
        dishPrice: 0,
        dishPriceWithVariants: 0,
        isBundle: false,
        bundleParentId: parentCartItemId,
        bundleTemplateId: product?.id,
        bundleSignature,
        bundleStepId: selection?.stepId,
        bundleStepName: selection?.stepName,
        variantSelected,
        note: note || null,
        allocatedPrice,
        allocatedPriceWithVariants,
        isSplit,
      };

      if (adjustmentValue !== 0) {
        subItem.priceAdjustment = adjustmentValue;
      }

      const isSelectedSplitBundle =
        dish?.isBundle === true && dish?.bundleConfig?.isSplit === true;
      if (!isSelectedSplitBundle || !Array.isArray(dish?.bundleSelections)) {
        return subItem;
      }

      const splitParentCartItemId = randomId();
      const splitParentItem = {
        ...subItem,
        id: dish?.id,
        dishName: dish?.dishName,
        dishDocketName: dish?.dishDocketName || dish?.dishName,
        description: dish?.description || dish?.dishDescription || "",
        category: dish?.category || "",
        categoryId: dish?.categoryId || "",
        cartItemId: splitParentCartItemId,
        quantity: 1,
        dishPrice: 0,
        dishPriceWithVariants: 0,
        isBundle: true,
        isSplit: true,
        bundleParentId: parentCartItemId,
        parentBundleId: parentCartItemId,
        variantSelected,
        note: note || null,
        bundleStepId: selection?.stepId,
        bundleStepName: selection?.stepName,
      };

      const preparedSplitChildren = dish.bundleSelections
        .filter((splitSelection) => splitSelection?.selectedItem?.dish)
        .map((splitSelection) => {
          const splitDish = splitSelection.selectedItem.dish;
          const splitAdjustmentRaw = splitSelection?.selectedItem?.priceAdjustment;
          const splitAdjustment = parseStepPriceAdjustment(splitAdjustmentRaw);
          const splitVariants =
            splitSelection?.selectedItem?.variants ||
            splitSelection?.selectedItem?.variantSelected ||
            [];
          const nestedSteps = getBundleStepsFromConfig(splitDish?.bundleConfig);
          const splitBundleStep =
            nestedSteps.find((step) => step?.id === splitSelection?.stepId) || null;
          const splitBaseWeight = resolveBundleStepBaseWeight(
            splitDish,
            splitVariants,
            splitBundleStep
          );
          const splitLineExtras = getBundleStepLineExtras(
            splitAdjustmentRaw,
            splitVariants
          );
          return {
            splitDish,
            splitSelection,
            splitAdjustment,
            splitVariants,
            splitBaseWeight,
            splitLineExtras,
          };
        });
      const splitBaseWeightTotal = preparedSplitChildren.reduce(
        (sum, child) => sum + child.splitBaseWeight,
        0
      );
      const splitParentAllocatedPrice = Number(splitParentItem.allocatedPrice || 0);
      const splitChildCount = preparedSplitChildren.length;
      const splitChildItems = preparedSplitChildren.map((child) => {
        const splitAllocated = computeStepAllocatedPrices({
          bundleBasePrice: splitParentAllocatedPrice,
          baseWeight: child.splitBaseWeight,
          sumBaseWeights: splitBaseWeightTotal,
          selectedStepCount: splitChildCount,
          lineExtras: child.splitLineExtras,
        });
        return {
          id: child.splitDish?.id,
          dishName: child.splitDish?.dishName,
          dishDocketName:
            child.splitDish?.dishDocketName || child.splitDish?.dishName,
          description:
            child.splitDish?.description || child.splitDish?.dishDescription || "",
          category: child.splitDish?.category || "",
          categoryId: child.splitDish?.categoryId || "",
          variants: child.splitDish?.variants || [],
          cartItemId: randomId(),
          quantity: 1,
          dishPrice: 0,
          dishPriceWithVariants: 0,
          isBundle: false,
          bundleParentId: splitParentCartItemId,
          bundleTemplateId: product?.id,
          bundleSignature,
          bundleStepId: child.splitSelection?.stepId || selection?.stepId,
          bundleStepName: child.splitSelection?.stepName || selection?.stepName,
          variantSelected: child.splitVariants,
          note: child.splitSelection?.selectedItem?.note || null,
          allocatedPrice: splitAllocated.allocatedPrice,
          allocatedPriceWithVariants: splitAllocated.allocatedPriceWithVariants,
          isSplit: true,
          parentBundleId: parentCartItemId,
          ...(child.splitAdjustment !== 0
            ? { priceAdjustment: child.splitAdjustment }
            : {}),
        };
      });

      return [splitParentItem, ...splitChildItems];
    });

    return [parentItem, ...subItems];
  };

  const buildBundleItems = () => {
    const requestedInstances = Math.max(1, Number(quantity) || 1);
    const bundleItems = [];

    for (let i = 0; i < requestedInstances; i += 1) {
      bundleItems.push(...buildSingleBundleInstance());
    }

    return bundleItems;
  };

  const handleAddBundleToCart = () => {
    if (!isAllStepsSelected) {
      setShowValidation(true);
      return;
    }

    const bundleItems = buildBundleItems();
    dispatch(setCartItems([...cartItems, ...bundleItems]));
    if (kitchen?.kitchenId) {
      dispatch(addKitchenIdToCart(kitchen.kitchenId));
    }
    toggleShowModal();
  };

  const renderStepsView = () => (
    <Box sx={{ padding: "16px 24px 24px", "@media screen and (max-width: 768px)": { padding: "16px" } }}>
      {normalizedSteps.map((step) => {
        const selection = stepSelections[step._stepId];
        const hasError = showValidation && !selection?.dish;
        return (
          <Box
            key={step._stepId}
            onClick={() => onStepPress(step)}
            sx={{
              border: `1px solid ${hasError ? "#ef4444" : backgroundTertiary}`,
              borderRadius: "8px",
              padding: "12px 14px",
              minHeight: "84px",
              marginBottom: "10px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: textBody,
              background: backgroundSecondary,
              boxShadow: "none",
              "@media screen and (max-width: 768px)": {
                minHeight: "80px",
              },
            }}
          >
            <Box>
              <Box
                sx={{
                  fontWeight: 600,
                  color: hasError ? "#ef4444" : brandTertiary,
                  fontSize: selection?.dish ? "14px" : isMobile ? "16px" : "18px",
                }}
              >
                {step._stepName}
              </Box>
              <Box
                sx={{
                  fontSize: "14px",
                  color: hasError ? "#ef4444" : textBody,
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                <span style={{ fontWeight: selection?.dish ? 600 : 400 }}>
                  {selection?.dish ? (
                    <span style={{ fontSize: isMobile ? "16px" : "18px" }}>
                      {selection.dish.dishName}
                    </span>
                  ) : (
                    " "
                  )}
                </span>
                {selection?.dish && formatPriceAdjustment(selection?.priceAdjustment) && (
                  <span style={{ fontWeight: 600 }}>
                    ({formatPriceAdjustment(selection?.priceAdjustment)})
                  </span>
                )}
              </Box>
              {selection?.dish &&
                Array.isArray(selection?.variantSelected) &&
                selection.variantSelected.length > 0 && (
                  <Box sx={{ marginTop: "2px", fontSize: "14px", color: textBody, opacity: 0.9 }}>
                    {selection.variantSelected
                      .map((variant) => {
                        const chargeText =
                          variant?.extraCharge && formatVariantCharge(variant?.charge)
                            ? ` (${formatVariantCharge(variant?.charge)})`
                            : "";
                        return `${variant?.name}${chargeText}`;
                      })
                      .join(", ")}
                  </Box>
                )}
              {selection?.dish && selection?.note && (
                <Box sx={{ fontSize: "14px", color: textBody, opacity: 0.9, marginTop: "2px" }}>
                  Note: {selection.note}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                color: hasError ? "#ef4444" : brandPrimary,
                minHeight: "24px",
                minWidth: "24px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
                "@media screen and (max-width: 768px)": {
                  minHeight: "20px",
                  minWidth: "20px",
                },
              }}
            >
              {selection?.dish ? (
                <Box sx={{ display: "flex", "& svg": { width: isMobile ? "22px" : "24px", height: isMobile ? "22px" : "24px" } }}>
                  <SuccessIcon />
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: hasError ? "#ef4444" : brandPrimary,
                    }}
                  >
                    Choose Item
                  </Box>
                  <ArowSmallIcon size={isMobile ? "small" : "middle"} />
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const renderStepPickerView = () => (
    <Box>
      <StepHeaderContainer
        headerModalBackground={headerModalBackground}
        backgroundTertiary={backgroundTertiary}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <StepHeaderIconContainer
          onClick={() => {
            setCurrentView("steps");
            setPickerSearchTerm("");
            setIsPickerSearchOpen(false);
          }}
          brandTertiary={brandTertiary}
          headerModalBackground={headerModalBackground}
        >
          <LeftIcon size={isMobile ? "small" : "large"} />
        </StepHeaderIconContainer>
        {(!isPickerSearchOpen || isMobile) && (
          <StepHeaderTitle brandFontStyle={brandFontStyle} brandAccent={brandAccent}>
            {selectedStep?._stepName || "Select Item"}
          </StepHeaderTitle>
        )}
        <Box
          sx={{
            flex: isPickerSearchOpen && !isMobile ? 1 : "unset",
            marginLeft: isPickerSearchOpen && !isMobile ? "8px" : 0,
          }}
        >
          <Search
            openSearch={isPickerSearchOpen}
            setOpenSearch={setIsPickerSearchOpen}
            setSearchTerm={setPickerSearchTerm}
            disableAnimation={true}
            expandInContainer={isPickerSearchOpen && !isMobile}
            fieldBorder={fieldBorder}
            fieldBackground={fieldBackground}
            textBody={fieldFilledText}
            fieldPlaceholderText={fieldPlaceholderText}
            brandTertiary={brandTertiary}
            brandPrimary={brandPrimary}
          />
        </Box>
      </StepHeaderContainer>

      <Box
        sx={{
          padding: "12px 24px 24px",
          "@media screen and (max-width: 768px)": { padding: "12px 16px 24px" },
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "12px",
        }}
      >
        {filteredAvailableDishes.map((dish) => {
          const dishOutOfStock =
            dish?.stockDetails?.isEnabled && dish?.stockDetails?.quantity === 0;
          const isDishSoldOut = dish?.dishStatus === dishSoldOut || dishOutOfStock;
          const promoUnavailable = isDishPromoUnavailable(dish);
          const isUnavailable = isDishSoldOut || promoUnavailable;
          const unavailableLabel = isDishSoldOut ? "Sold Out" : "Unavailable";
          const priceAdjustment = selectedStep ? findPriceAdjustment(selectedStep, dish) : 0;
          const adjustmentText = formatPriceAdjustment(priceAdjustment);
          const dishPromoAvailabilityText = formatPromoAvailabilityText(dish?.promo);
          return (
            <MenuItemContainer
              key={`${selectedStep?._stepId}-${dish?.id}`}
              onClick={() => !isUnavailable && onStepItemPress(dish)}
              cardBackground={cardBackground}
              style={{
                cursor: isUnavailable ? "not-allowed" : "pointer",
                pointerEvents: "auto",
              }}
            >
              <MainContainer>
                <TextTopBox imageUrl={dish?.remoteUri}>
                  <NameContainer soldOut={isUnavailable} brandTertiary={brandTertiary}>
                    {dish?.dishName}
                  </NameContainer>
                  {dishPromoAvailabilityText && (
                    <PromoAvailabilityText
                      brandTertiary={brandTertiary}
                      $compact
                      style={{ opacity: isUnavailable ? 0.5 : 1 }}
                    >
                      {dishPromoAvailabilityText}
                    </PromoAvailabilityText>
                  )}
                  <CardDescription soldOut={isUnavailable} textBody={textBody}>
                    {dish?.dishDescription ? dish.dishDescription.replaceAll("\\n", "\n") : ""}
                  </CardDescription>
                </TextTopBox>
                <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                  {adjustmentText && (
                    <PriceContainer soldOut={isUnavailable} textBody={textBody}>
                      {adjustmentText}
                    </PriceContainer>
                  )}
                    {isUnavailable && (
                      <SoldOutContainer>
                        {adjustmentText && <SoldOutCircle textBody={textBody} />}
                        <SoldOutText textBody={textBody} backgroundPrimary={backgroundPrimary}>
                          {unavailableLabel}
                        </SoldOutText>
                      </SoldOutContainer>
                    )}
                  </Box>
                </Box>
              </MainContainer>
              {dish?.remoteUri && (
                <ImageContainer>
                  <ImageSkeleton
                    hidden={Boolean(pickerImageLoadedMap[dish?.id])}
                    skeletonBase={skeletonBase}
                    skeletonHighlight={skeletonHighlight}
                  />
                  <BackgroundImage
                    src={dish.remoteUri}
                    alt={dish?.dishName || ""}
                    loaded={Boolean(pickerImageLoadedMap[dish?.id])}
                    soldOut={isUnavailable}
                    loading="lazy"
                    decoding="async"
                    onLoad={() =>
                      setPickerImageLoadedMap((prev) => ({ ...prev, [dish?.id]: true }))
                    }
                    onError={() =>
                      setPickerImageLoadedMap((prev) => ({ ...prev, [dish?.id]: true }))
                    }
                  />
                </ImageContainer>
              )}
            </MenuItemContainer>
          );
        })}
        {filteredAvailableDishes.length === 0 && (
          <Box sx={{ fontSize: "14px", color: textBody, opacity: 0.8, padding: "8px 4px" }}>
            No matching items found.
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Modal open={showModal} onClose={handleModalClose} closeAfterTransition>
        <Fade in={showModal}>
          <ModalContainer
            headerModalBackground={headerModalBackground}
            isMobile={isMobile}
            style={{
              minHeight: isMobile ? "100%" : "90%",
              height: isMobile ? "100%" : "90%",
              maxHeight: isMobile ? "100%" : "90%",
              overflowX: "hidden",
            }}
          >
            <ModalScrollContainer
              ref={contentRef}
              style={{ minHeight: "auto", overflowX: "hidden" }}
            >
              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {currentView === "steps" && (
                  <>
                    <Box sx={{ position: "sticky", right: 0, top: 0, zIndex: 13 }}>
                      <ModalIconContainer
                        onClick={toggleShowModal}
                        headerModalBackground={headerModalBackground}
                        brandPrimary={brandPrimary}
                      >
                        <CloseIcon size={"middle"} />
                      </ModalIconContainer>
                    </Box>
                    {product?.remoteUri && (
                      <Box
                        sx={{
                          width: "100%",
                          height: "328px",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <ImageSkeleton
                          hidden={heroImageLoaded}
                          skeletonBase={skeletonBase}
                          skeletonHighlight={skeletonHighlight}
                        />
                        <img
                          src={product.remoteUri}
                          alt=""
                          loading="eager"
                          decoding="async"
                          onLoad={() => setHeroImageLoaded(true)}
                          onError={() => setHeroImageLoaded(true)}
                          style={{
                            objectFit: "cover",
                            width: "100%",
                            height: "328px",
                            display: "block",
                            opacity: heroImageLoaded ? 1 : 0,
                            transition: "opacity 220ms ease",
                          }}
                        />
                      </Box>
                    )}
                    <DishName
                      hasBorder={hasBorder}
                      brandFontStyle={brandFontStyle}
                      brandAccent={brandAccent}
                      headerModalBackground={headerModalBackground}
                      backgroundTertiary={backgroundTertiary}
                    >
                      <DishNameEllips hasBorder={hasBorder}>{product?.dishName}</DishNameEllips>
                    </DishName>
                    <TextBox
                      backgroundTertiary={backgroundTertiary}
                      headerModalBackground={headerModalBackground}
                    >
                      {bundlePromoAvailabilityText && (
                        <PromoAvailabilityText brandTertiary={brandTertiary}>
                          {bundlePromoAvailabilityText}
                        </PromoAvailabilityText>
                      )}
                      {product?.dishDescription && (
                        <DescriptionText textBody={textBody}>
                          {product.dishDescription?.replaceAll("\\n", "\n")}
                        </DescriptionText>
                      )}
                      <DishPrice textBody={textBody}>
                        {formatCurrency(product?.dishPrice)}
                      </DishPrice>
                    </TextBox>
                    {renderStepsView()}
                  </>
                )}

                <Slide
                  in={currentView === "picker"}
                  direction="left"
                  mountOnEnter
                  unmountOnExit
                  timeout={220}
                >
                  <Box
                    sx={{
                      background: headerModalBackground,
                    }}
                  >
                    {renderStepPickerView()}
                  </Box>
                </Slide>
              </Box>

              {currentView === "steps" && (
                <Box
                  sx={{
                    borderTopWidth: "1px",
                    borderTopStyle: "solid",
                    borderTopColor: backgroundSecondary,
                    position: "sticky",
                    zIndex: 14,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: headerModalBackground,
                    padding: "20px 24px",
                    minHeight: "89px",
                    "@media screen and (max-width: 768px)": {
                      minHeight: "85px",
                      padding: "20px 16px",
                    },
                  }}
                >
                  {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                    isKitchenOpen &&
                    onlineOrdersConfig?.onlineOrderingPaused === false &&
                    !cannotOrderBundle && (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <QuantityContainer>
                          <Quantity
                            decrement={decrement}
                            increment={increment}
                            label={quantity}
                            isRemoveDisabled={quantity === 1}
                            size={isMobile ? "small" : "medium"}
                          />
                        </QuantityContainer>
                        <Button
                          onClick={handleAddBundleToCart}
                          buttonType={BUTTON_TYPE_CLASSES.base}
                        >
                          Add to Cart ({formatCurrency(calculatedTotal)})
                        </Button>
                      </Box>
                    </>
                  )}
                  {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                    isKitchenOpen &&
                    onlineOrdersConfig?.onlineOrderingPaused === false &&
                    cannotOrderBundle && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <QuantityContainer>
                          <Quantity
                            isRemoveDisabled={true}
                            isAddDisabled={true}
                            label={quantity}
                            size={isMobile ? "small" : "medium"}
                          />
                        </QuantityContainer>
                        <Button
                          disabled={true}
                          buttonType={BUTTON_TYPE_CLASSES.base}
                        >
                          {unavailableButtonLabel} ({formatCurrency(calculatedTotal)})
                        </Button>
                      </Box>
                    )}
                  {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                    !isKitchenOpen && (
                      <Status
                        status="close"
                        primaryText="Closed"
                        secondaryText={`Opens ${findNextOpeningTime?.day} at ${findNextOpeningTime?.time}`}
                        forModalStatus={true}
                      />
                    )}
                </Box>
              )}
            </ModalScrollContainer>
          </ModalContainer>
        </Fade>
      </Modal>

      {isStepItemModalOpen && selectedDishForModal && (
        <TransitionsModal
          product={selectedDishForModal}
          showModal={isStepItemModalOpen}
          toggleShowModal={() => {
            setIsStepItemModalOpen(false);
            setSelectedDishForModal(null);
          }}
          kitchen={kitchen}
          onConfirmSelection={onStepItemConfigured}
          actionLabel="Select Item"
          hideQuantityControls={true}
          bundlePriceAdjustment={selectedDishForModal?._priceAdjustment}
        />
      )}
    </>
  );
};
